import type { FormikProps } from "formik";

/**
 * Recursively collects all dot-notation paths from a nested form values structure.
 * Used to programmatically mark all fields as "touched" in Formik so validation
 * errors become visible.
 *
 * Handles:
 * - Primitive values → path pushed directly
 * - Arrays → indexed paths (e.g. "sections[0].name")
 * - Objects → dot-notation paths (e.g. "sections[0].questionnaire[0].question")
 * - Special case: "fields" key with type 10 (document upload question type)
 */
export const collectTouchedPaths = (values: unknown, basePath = ""): string[] => {
	const paths: string[] = [];

	if (!values) {
		return paths;
	}

	// if answer is an array of values
	if (Array.isArray(values)) {
		// if we are at the base of the array and its empty or not an array of objects
		// mark the general field
		if (values.length === 0 || typeof values[0] !== "object") {
			// mark the array itself
			paths.push(basePath);
		}

		// now process each value in the array
		values.forEach((item, index) => {
			// if an objecttype, we need to keep going through the path to get to the 'value'
			if (typeof item === "object" && item !== null) {
				paths.push(...collectTouchedPaths(item, `${basePath}[${index}]`));
			} else {
				// primitive value: push the indexed path
				paths.push(`${basePath}[${index}]`);
			}
		});
	} else if (typeof values === "object") {
		Object.entries(values as Record<string, unknown>).forEach(([key, val]) => {
			const path = basePath ? `${basePath}.${key}` : key;

			if (key === "fields" && (values as Record<string, unknown>).type === 10) {
				paths.push(path);

				if ((val as Record<string, unknown>)?.allow_document_categories) {
					paths.push(`${path}.allow_document_categories`);
				}
			} else {
				paths.push(path);
			}

			paths.push(...collectTouchedPaths(val, path));
		});
	}

	return paths;
};

/**
 * Recursively touches all fields in a nested Formik structure so that
 * validation errors become visible to the user.
 */
export const touchFieldsRecursively = (
	values: unknown,
	basePath: string,
	formik: Pick<FormikProps<unknown>, "setFieldTouched">
): void => {
	const pathsToTouch = collectTouchedPaths(values, basePath);
	pathsToTouch.forEach((path) => formik.setFieldTouched(path, true, false));
};
